<template>
  <Modal
    :value="value"
    @input="v => $emit('input', v)"
    :title="modalTitle"
    width="820px"
    :mask-closable="false"
    class-name="ai-select-image-modal"
  >
    <!-- 顶部语言 Tab -->
    <div class="ai-select-lang-tabs">
      <span
        v-for="tab in langTabs"
        :key="tab.value"
        :class="['ai-select-lang-tab', activeLang === tab.value ? 'active' : '']"
        @click="switchLang(tab.value)"
      >
        {{ tab.label }}
        <em
          v-if="getSelectedCountByLang(tab.value) > 0"
          class="ai-select-lang-tab-count"
        >{{ getSelectedCountByLang(tab.value) }}</em>
      </span>
    </div>

    <!-- 当前操作语种提示 -->
    <div class="ai-select-current-lang">
      当前操作语种：<strong>{{ activeLangLabel }}</strong>
      <span class="ai-select-total-count">
        <Checkbox class="ai-select-select-all" :value="isAllLangSelected" :indeterminate="isIndeterminateLang"
          @on-change="toggleSelectAllCurrentLang">
         全选当前{{ dimension === 'spu' ? '语言' : '图套' }}
        </Checkbox>
      </span>
    </div>

    <!-- SKU: 图套标签行 -->
    <div v-if="dimension === 'sku'" class="ai-select-set-bar">
      <template v-if="currentLangImageSets.length > 0">
        <span
          v-for="imgSet in currentLangImageSets"
          :key="imgSet.productGoodsImageSetId"
          :class="['ai-select-set-tag', activeSetId === imgSet.productGoodsImageSetId ? 'active' : '']"
          @click="switchImageSet(imgSet.productGoodsImageSetId)"
        >
          {{ imgSet.setName }}
          <em
            v-if="imgSet.setType"
            class="ai-select-set-type-badge"
            :style="{ background: getSetTypeBadgeColor(imgSet.setType) }"
          >{{ getSetTypeLabel(imgSet.setType) }}</em>
        </span>
      </template>
      <span v-else class="ai-select-set-empty-tip">
        当前语言下暂无图套，请先在图套弹窗中创建
      </span>
    </div>

    <!-- 图片网格 -->
    <div class="ai-select-image-grid">
      <template v-if="dimension === 'spu'">
        <div
          v-for="(img, idx) in currentSpuImages"
          :key="`spu-${idx}-${img.productImageId || img.path}`"
          class="ai-select-image-item"
          :class="{ selected: isSelected(buildSpuImage(img)) }"
          @click="toggleSelect(buildSpuImage(img))"
        >
          <Checkbox
            class="ai-select-image-checkbox"
            :value="isSelected(buildSpuImage(img))"
            @click.native.stop
            @on-change="toggleSelect(buildSpuImage(img))"
          />
          <img :src="filenodeViewTargetUrl + img.path" />
        </div>
        <div v-if="currentSpuImages.length === 0" class="ai-select-empty">
          当前语言下暂无图片
        </div>
      </template>

      <template v-else>
        <div
          v-for="(imgPath, idx) in currentSetImages"
          :key="`sku-${idx}-${imgPath}`"
          class="ai-select-image-item"
          :class="{ selected: isSelected(buildSkuImage(imgPath)) }"
          @click="toggleSelect(buildSkuImage(imgPath))"
        >
          <Checkbox
            class="ai-select-image-checkbox"
            :value="isSelected(buildSkuImage(imgPath))"
            @click.native.stop
            @on-change="toggleSelect(buildSkuImage(imgPath))"
          />
          <img :src="filenodeViewTargetUrl + imgPath" />
        </div>
        <div
          v-if="currentLangImageSets.length > 0 && currentSetImages.length === 0"
          class="ai-select-empty"
        >
          当前图套下暂无图片
        </div>
      </template>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="ai-select-footer">
      <span class="ai-select-summary">共已选 {{ selectedList.length }} 张图片</span>
      <Button @click="handleCancel">取消</Button>
      <Button type="primary" @click="handleConfirm">确定</Button>
    </div>
  </Modal>
</template>

<script>
/**
 * @description AI生图 - 二级选图弹窗（SPU/SKU 共用）
 * @author 前端开发
 * @date 2026-07-21
 * @功能说明
 *   1. 支持 SPU 维度：2 层结构（语言 → 图片）
 *   2. 支持 SKU 维度：3 层结构（语言 → 图套 → 图片）
 *   3. 支持跨语言/跨图套累计多选，切换 tab 不清空已选
 *   4. 通过 preSelected 支持已选图片的回显
 *   5. 点击"确定"通过 on-ok 事件把选中数组带回主弹窗
 * @选中数据结构
 *   SPU: { path, imageId, language, setName }                            (imageId 取自后端 productImageId)
 *   SKU: { path, imageId, productGoodsImageSetId, language, setName, setType }
 *   (imageId 取自后端 productGoodsImageId； SKU 若图套 images 中该图无对应映射则为 undefined）
 */
export default {
  name: 'aiSelectImageModal',
  props: {
    /** 弹窗显示（配合 v-model 使用） */
    value: {
      type: Boolean,
      default: false
    },
    /** 维度：'spu' 或 'sku' */
    dimension: {
      type: String,
      default: 'spu',
      validator: v => ['spu', 'sku'].includes(v)
    },
    /** SPU 多语言图片数据（dimension='spu' 时使用） */
    multiLangImageData: {
      type: Array,
      default: () => []
    },
    /** SKU 图套列表（dimension='sku' 时使用） */
    skuImageSetList: {
      type: Array,
      default: () => []
    },
    /** 已选图片列表（用于回显，由主弹窗透传） */
    preSelected: {
      type: Array,
      default: () => []
    },
    /** 文件服务器查看地址前缀 */
    filenodeViewTargetUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      /** 当前激活语言 */
      activeLang: 'common',
      /** SKU 维度：当前激活图套 id */
      activeSetId: '',
      /** 累计选中列表（跨 tab/图套） */
      selectedList: []
    };
  },
  computed: {
    /** 弹窗标题 */
    modalTitle() {
      return this.dimension === 'spu'
        ? '选用已有图案 —— SPU 图片'
        : '选用已有图案 —— SKU 图套图片';
    },
    /** 语言 tab 数据 */
    langTabs() {
      return [
        { value: 'common', label: '通用' },
        { value: 'en', label: '英语 (EN)' },
        { value: 'fr', label: '法语 (FR)' },
        { value: 'de', label: '德语 (DE)' },
        { value: 'ru', label: '俄语 (RU)' },
        { value: 'nl', label: '荷兰语 (NL)' }
      ];
    },
    /** 当前激活语言的中文名 */
    activeLangLabel() {
      const tab = this.langTabs.find(t => t.value === this.activeLang);
      return tab ? tab.label : this.activeLang;
    },
    /** SPU 当前语言下的图片列表 */
    currentSpuImages() {
      if (this.dimension !== 'spu') return [];
      const group = (this.multiLangImageData || []).find(g => g.language === this.activeLang);
      return group ? (group.images || []) : [];
    },
    /** SKU 当前语言下的图套列表 */
    currentLangImageSets() {
      if (this.dimension !== 'sku') return [];
      return (this.skuImageSetList || []).filter(s => s.language === this.activeLang);
    },
    /** SKU 当前激活图套对象 */
    currentActiveSet() {
      if (this.dimension !== 'sku') return null;
      const list = this.currentLangImageSets;
      if (list.length === 0) return null;
      const found = list.find(s => s.productGoodsImageSetId === this.activeSetId);
      return found || list[0];
    },
    /** SKU 当前图套下的图片路径数组 */
    currentSetImages() {
      if (!this.currentActiveSet) return [];
      return this.currentActiveSet.images || [];
    },
    /** 当前语言下所有可选图片（SPU: 当前语言图片；SKU: 当前语言所有图套图片） */
    currentLangAllImages() {
      if (this.dimension === 'spu') {
        return this.currentSpuImages.map(img => this.buildSpuImage(img));
      }
      // SKU: 当前激活图套下的图片
      return this.currentSetImages.map(imgPath => this.buildSkuImage(imgPath));
    },
    /** 当前语言下是否全部选中 */
    isAllLangSelected() {
      const list = this.currentLangAllImages;
      if (list.length === 0) return false;
      return list.every(item => this.isSelected(item));
    },
    /** 当前语言下是否半选（部分选中） */
    isIndeterminateLang() {
      const list = this.currentLangAllImages;
      if (list.length === 0) return false;
      const selectedCount = list.filter(item => this.isSelected(item)).length;
      return selectedCount > 0 && selectedCount < list.length;
    }
  },
  watch: {
    /**
     * 弹窗打开时初始化状态：回显 preSelected 到 selectedList，
     * 并重置 activeLang / activeSetId 为默认值
     */
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.selectedList = this.deepCopySelected(this.preSelected || []);
          this.activeLang = 'common';
          if (this.dimension === 'sku') {
            const firstSet = this.currentLangImageSets[0];
            this.activeSetId = firstSet ? firstSet.productGoodsImageSetId : '';
          }
        }
      }
    },
    /** 语言切换时，SKU 维度自动激活该语言下第一个图套 */
    activeLang() {
      if (this.dimension === 'sku') {
        const firstSet = this.currentLangImageSets[0];
        this.activeSetId = firstSet ? firstSet.productGoodsImageSetId : '';
      }
    }
  },
  methods: {
    /** 深拷贝选中列表（避免与父组件 preSelected 共享引用） */
    deepCopySelected(list) {
      return (list || []).map(item => ({ ...item }));
    },
    /**
     * 构建选中项的唯一标识
     * imageId 优先，否则用 `${dimension}-${path}`(兼图套) 兑底
     */
    imageKey(item) {
      if (item.imageId !== undefined && item.imageId !== null && item.imageId !== '') {
        return `${this.dimension}-id-${item.imageId}`;
      }
      if (this.dimension === 'spu') {
        return `spu-${item.language || 'common'}-${item.path}`;
      }
      return `sku-${item.productGoodsImageSetId || ''}-${item.path}`;
    },
    /**
     * 构建 SPU 图片选中项
     * @param {Object} img 图片对象（含 path/productImageId）
     */
    buildSpuImage(img) {
      return {
        path: img.path,
        imageId: img.productImageId, // 后端 SPU 图片主键为 productImageId，前端统一存为 imageId
        language: this.activeLang,
        setName: '通用'
      };
    },
    /**
     * 构建 SKU 图片选中项
     * @param {string} imgPath 图片路径（当前 SKU 图套 images 存的是纯字符串）
     *
     * imageId 通过图套旁路字段 imageIdMap 反查（loadImageSetList 包建，path -> productGoodsImageId），
     * 若后端未返回对应关系，则 imageId 为 undefined
     */
    buildSkuImage(imgPath) {
      const set = this.currentActiveSet;
      const idMap = (set && set.imageIdMap) || {};
      return {
        path: imgPath,
        imageId: idMap[imgPath], // 后端 SKU 图片主键为 productGoodsImageId，前端统一存为 imageId
        productGoodsImageSetId: set ? set.productGoodsImageSetId : '',
        language: this.activeLang,
        setName: set ? set.setName : '',
        setType: set ? set.setType : ''
      };
    },
    /** 判断某图片项是否已选中 */
    isSelected(item) {
      const key = this.imageKey(item);
      return this.selectedList.some(i => this.imageKey(i) === key);
    },
    /** 切换选中状态 */
    toggleSelect(item) {
      const key = this.imageKey(item);
      const idx = this.selectedList.findIndex(i => this.imageKey(i) === key);
      if (idx === -1) {
        this.selectedList.push(item);
      } else {
        this.selectedList.splice(idx, 1);
      }
    },
    /** 切换语言 tab */
    switchLang(lang) {
      this.activeLang = lang;
    },
    /** 切换图套（SKU维度） */
    switchImageSet(setId) {
      this.activeSetId = setId;
    },
    /** 获取指定语言下已选图片数量 */
    getSelectedCountByLang(lang) {
      return this.selectedList.filter(item => item.language === lang).length;
    },
    /** 全选/取消全选当前语言下所有图片 */
    toggleSelectAllCurrentLang(checked) {
      const list = this.currentLangAllImages;
      if (checked) {
        list.forEach(item => {
          if (!this.isSelected(item)) {
            this.selectedList.push(item);
          }
        });
      } else {
        const keysToRemove = new Set(list.map(item => this.imageKey(item)));
        this.selectedList = this.selectedList.filter(item => !keysToRemove.has(this.imageKey(item)));
      }
    },
    /** 获取图套类型显示文本 */
    getSetTypeLabel(setType) {
      const map = { '1': '网图', '2': '非实拍自修', '3': '实拍自修' };
      return map[String(setType)] || String(setType);
    },
    /** 获取图套类型 badge 颜色 */
    getSetTypeBadgeColor(setType) {
      const map = { '1': '#f90', '2': '#19be6b', '3': '#2d8cf0' };
      return map[String(setType)] || '#999';
    },
    /** 点击"取消"：关闭弹窗，不带出选中数据 */
    handleCancel() {
      this.$emit('input', false);
      this.$emit('on-cancel');
    },
    /** 点击"确定"：把累计选中数组通过 on-ok 事件带出 */
    handleConfirm() {
      this.$emit('on-ok', this.deepCopySelected(this.selectedList));
      this.$emit('input', false);
    }
  }
};
</script>

<style scoped lang="less">
.ai-select-lang-tabs {
  display: flex;
  border-bottom: 1px solid #e8eaec;
  margin-bottom: 12px;

  .ai-select-lang-tab {
    position: relative;
    padding: 8px 14px;
    cursor: pointer;
    color: #515a6e;
    font-size: 14px;
    user-select: none;
    border-radius: 4px 4px 0 0;

    &:hover {
      color: #2d8cf0;
    }

    &.active {
      color: #fff;
      background: #2d8cf0;
    }

    .ai-select-lang-tab-count {
      display: inline-block;
      min-width: 18px;
      height: 18px;
      line-height: 18px;
      margin-left: 4px;
      padding: 0 4px;
      background: #ed4014;
      color: #fff;
      font-style: normal;
      font-size: 12px;
      border-radius: 9px;
      text-align: center;
    }
  }
}

.ai-select-current-lang {
  padding: 0 4px 8px;
  color: #515a6e;
  font-size: 13px;
  overflow: hidden;

  strong {
    color: #17233d;
    margin-right: 8px;
  }

  .ai-select-select-all {
    margin-left: 12px;
  }

  .ai-select-total-count {
    float: right;
    // color: #515a6e;
  }
}

.ai-select-set-bar {
  padding: 8px;
  background: #f8f8f9;
  border-radius: 4px;
  margin-bottom: 10px;
  min-height: 40px;

  .ai-select-set-tag {
    display: inline-block;
    padding: 4px 10px;
    margin-right: 8px;
    margin-bottom: 4px;
    background: #fff;
    border: 1px solid #dcdee2;
    border-radius: 4px;
    cursor: pointer;
    color: #515a6e;
    font-size: 13px;
    user-select: none;

    &:hover {
      border-color: #2d8cf0;
      color: #2d8cf0;
    }

    &.active {
      background: #2d8cf0;
      border-color: #2d8cf0;
      color: #fff;
    }

    .ai-select-set-type-badge {
      display: inline-block;
      margin-left: 6px;
      padding: 0 6px;
      line-height: 16px;
      font-size: 12px;
      color: #fff;
      border-radius: 8px;
      font-style: normal;
    }
  }

  .ai-select-set-empty-tip {
    color: #999;
    font-size: 13px;
  }
}

.ai-select-image-grid {
  min-height: 260px;
  max-height: 380px;
  overflow-y: auto;
  padding: 4px;

  .ai-select-image-item {
    position: relative;
    display: inline-block;
    width: 120px;
    height: 120px;
    margin: 0 8px 8px 0;
    border: 2px solid #dcdee2;
    border-radius: 4px;
    cursor: pointer;
    overflow: hidden;
    vertical-align: top;
    transition: all 0.2s;

    &:hover {
      border-color: #2d8cf0;
    }

    &.selected {
      border-color: #2d8cf0;
      box-shadow: 0 0 6px rgba(45, 140, 240, 0.5);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .ai-select-image-checkbox {
      position: absolute;
      top: 4px;
      left: 4px;
      z-index: 2;
      background: rgba(255, 255, 255, 0.85);
      border-radius: 2px;
      padding: 0 2px;
    }
  }

  .ai-select-empty {
    padding: 40px 0;
    text-align: center;
    color: #999;
    font-size: 13px;
  }
}

.ai-select-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .ai-select-summary {
    flex: 1;
    text-align: left;
    color: #2d8cf0;
    font-size: 13px;
  }
}
</style>
